import React, { useEffect, useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { Button } from '../components/ui/button';
import { Loader2, Check, Music, FileAudio, MessageSquare, Search, Save, Sparkles, XCircle } from 'lucide-react';
import api from '../services/api';

const STALE_THRESHOLD_MS = 2 * 60 * 1000; // Show cancel after 2 minutes of no progress

const PHASE_CONFIG = {
  uploading: {
    title: "Uploading",
    icon: FileAudio,
    defaultMessage: "Uploading your track...",
    estimatedTime: "~10 seconds"
  },
  processing: {
    title: "Starting",
    icon: Music,
    defaultMessage: "Getting everything ready...",
    estimatedTime: "~5 seconds"
  },
  separating: {
    title: "Separating",
    icon: Music,
    defaultMessage: "Separating vocals from instrumental...",
    estimatedTime: "~45 seconds"
  },
  transcribing: {
    title: "Transcribing",
    icon: MessageSquare,
    defaultMessage: "Transcribing lyrics with AI...",
    estimatedTime: "~30 seconds"
  },
  detecting: {
    title: "Detecting",
    icon: Search,
    defaultMessage: "Scanning for curse words...",
    estimatedTime: "~5 seconds"
  },
  finalizing: {
    title: "Finalizing",
    icon: Save,
    defaultMessage: "Saving results...",
    estimatedTime: "~5 seconds"
  },
  complete: {
    title: "Complete",
    icon: Check,
    defaultMessage: "Your track is ready!",
    estimatedTime: ""
  },
  enhancing: {
    title: "Enhancing Lyrics",
    icon: Sparkles,
    defaultMessage: "Enhancing lyrics...",
    estimatedTime: "~5 seconds"
  },
  error: {
    title: "Error",
    icon: FileAudio,
    defaultMessage: "Something went wrong",
    estimatedTime: ""
  }
};

const PHASES_ORDER = ['uploading', 'separating', 'transcribing', 'detecting', 'finalizing', 'complete'];
const PHASES_ORDER_WITH_ENHANCING = ['uploading', 'separating', 'transcribing', 'detecting', 'finalizing', 'complete', 'enhancing'];

function PhaseIndicator({ phase, currentPhase, isComplete, isEnhancing, phasesOrder }) {
  const config = PHASE_CONFIG[phase] || PHASE_CONFIG.processing;
  const Icon = config.icon;
  
  const phaseIndex = phasesOrder.indexOf(phase);
  // Map 'processing' to 'uploading' since it's just a transitional state
  const mappedCurrentPhase = currentPhase === 'processing' ? 'uploading' : currentPhase;
  const currentIndex = phasesOrder.indexOf(mappedCurrentPhase);
  const isPast = phaseIndex < currentIndex || (isComplete && phase !== 'enhancing' && !isEnhancing) || (phase === 'enhancing' && isComplete && !isEnhancing);
  const isCurrent = (phase === mappedCurrentPhase && !isComplete) || (phase === 'enhancing' && isEnhancing);
  
  return (
    <div className={`flex items-center gap-2 py-1.5 transition-all duration-300 ${
      isPast ? 'text-green-500' : 
      isCurrent ? 'text-primary font-medium' : 
      'text-muted-foreground/50'
    }`}>
      <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300 ${
        isPast ? 'bg-green-500/20' : 
        isCurrent ? 'bg-primary/20' : 
        'bg-muted/30'
      }`}>
        {isPast ? (
          <Check className="w-3.5 h-3.5" />
        ) : isCurrent ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Icon className="w-3.5 h-3.5" />
        )}
      </div>
      <span className="text-sm">{config.title}</span>
    </div>
  );
}

export default function ProcessingModal({ isOpen, status, sessionId, onClose, onCancel, showEnhancing = false, isEnhancing = false }) {
  const [progress, setProgress] = useState({
    phase: status || 'uploading',
    message: 'Getting everything ready...',
    percent: 5,
    sub_progress: 0
  });
  const [errorMessage, setErrorMessage] = useState(null);
  const [showCancelButton, setShowCancelButton] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const pollIntervalRef = useRef(null);
  const lastProgressRef = useRef({ percent: 0, phase: '', timestamp: Date.now() });
  const staleCheckIntervalRef = useRef(null);
  
  const phasesOrder = showEnhancing ? PHASES_ORDER_WITH_ENHANCING : PHASES_ORDER;

  useEffect(() => {
    if (!isOpen || !sessionId) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      if (staleCheckIntervalRef.current) {
        clearInterval(staleCheckIntervalRef.current);
        staleCheckIntervalRef.current = null;
      }
      return;
    }

    const pollStatus = async () => {
      try {
        const response = await api.get(`/sessions/${sessionId}/status`);
        const data = response.data;
        
        const newProgress = data.progress || {
          phase: data.status,
          percent: data.status === 'complete' ? 100 : progress.percent
        };
        
        // Check if progress has changed
        const hasProgressChanged = 
          newProgress.percent !== lastProgressRef.current.percent ||
          newProgress.phase !== lastProgressRef.current.phase;
        
        if (hasProgressChanged) {
          lastProgressRef.current = {
            percent: newProgress.percent,
            phase: newProgress.phase,
            timestamp: Date.now()
          };
          setShowCancelButton(false); // Hide cancel when progress is made
        }
        
        if (data.progress) {
          setProgress(data.progress);
        } else {
          setProgress(prev => ({
            ...prev,
            phase: data.status,
            percent: data.status === 'complete' ? 100 : prev.percent
          }));
        }
        
        if (data.status === 'error') {
          setErrorMessage(data.error_message || 'An error occurred during processing');
        }
        
        // Stop polling if complete or error
        if (data.status === 'complete' || data.status === 'error') {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          if (staleCheckIntervalRef.current) {
            clearInterval(staleCheckIntervalRef.current);
            staleCheckIntervalRef.current = null;
          }
        }
      } catch (error) {
        console.error('Error polling status:', error);
      }
    };

    // Stale detection - check every 30 seconds if progress is stuck
    const checkStale = () => {
      const timeSinceLastProgress = Date.now() - lastProgressRef.current.timestamp;
      if (timeSinceLastProgress >= STALE_THRESHOLD_MS) {
        setShowCancelButton(true);
      }
    };

    // Initial poll
    pollStatus();
    
    // Poll every 2 seconds
    pollIntervalRef.current = setInterval(pollStatus, 2000);
    
    // Check for stale progress every 30 seconds
    staleCheckIntervalRef.current = setInterval(checkStale, 30000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      if (staleCheckIntervalRef.current) {
        clearInterval(staleCheckIntervalRef.current);
        staleCheckIntervalRef.current = null;
      }
    };
  }, [isOpen, sessionId]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setProgress({
        phase: status || 'uploading',
        message: 'Getting everything ready...',
        percent: 5,
        sub_progress: 0
      });
      setErrorMessage(null);
      setShowCancelButton(false);
      setIsCancelling(false);
      lastProgressRef.current = { percent: 0, phase: '', timestamp: Date.now() };
    }
  }, [isOpen, status]);

  const handleCancel = async () => {
    if (!sessionId || isCancelling) return;
    
    // Stop polling immediately to prevent race conditions
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (staleCheckIntervalRef.current) {
      clearInterval(staleCheckIntervalRef.current);
      staleCheckIntervalRef.current = null;
    }
    
    setIsCancelling(true);
    try {
      await api.post(`/sessions/${sessionId}/cancel`);
      if (onCancel) {
        onCancel(sessionId);
      }
    } catch (error) {
      console.error('Failed to cancel processing:', error);
      setErrorMessage('Failed to cancel processing. Please try again.');
      // Restart polling if cancel failed
      if (isOpen && sessionId) {
        pollIntervalRef.current = setInterval(async () => {
          try {
            const response = await api.get(`/sessions/${sessionId}/status`);
            if (response.data.progress) {
              setProgress(response.data.progress);
            }
          } catch (e) {
            console.error('Error polling status:', e);
          }
        }, 2000);
      }
    } finally {
      setIsCancelling(false);
    }
  };

  const currentPhase = progress.phase || status || 'uploading';
  const phaseConfig = PHASE_CONFIG[currentPhase] || PHASE_CONFIG.processing;
  const isComplete = currentPhase === 'complete';
  const isError = currentPhase === 'error';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="processing-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            {!isComplete && !isError && (
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            )}
            {isComplete && <Check className="h-5 w-5 text-green-500" />}
            <span>
              {isComplete ? 'Processing Complete!' : 
               isError ? 'Processing Failed' : 
               'Processing Your Track'}
            </span>
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Progress bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{progress.message || phaseConfig.defaultMessage}</span>
              <span className="text-primary font-medium">{progress.percent}%</span>
            </div>
            <Progress value={progress.percent} className="w-full h-2" data-testid="processing-progress" />
          </div>
          
          {/* Phase timeline */}
          <div className="border rounded-lg p-3 bg-muted/20">
            <div className="space-y-0.5">
              {phasesOrder.map((phase) => (
                <PhaseIndicator 
                  key={phase} 
                  phase={phase} 
                  currentPhase={isEnhancing ? 'enhancing' : currentPhase}
                  isComplete={isComplete && !isEnhancing}
                  isEnhancing={isEnhancing}
                  phasesOrder={phasesOrder}
                />
              ))}
            </div>
          </div>
          
          {/* Error message */}
          {isError && errorMessage && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
              <p className="text-sm text-red-500">{errorMessage}</p>
            </div>
          )}
          
          {/* Estimated time hint */}
          {!isComplete && !isError && phaseConfig.estimatedTime && !showCancelButton && (
            <p className="text-xs text-center text-muted-foreground">
              This step typically takes {phaseConfig.estimatedTime}
            </p>
          )}
          
          {/* Cancel button - shows after 2 minutes of no progress */}
          {!isComplete && !isError && showCancelButton && (
            <div className="space-y-2">
              <p className="text-xs text-center text-amber-500">
                Processing seems to be taking longer than expected.
              </p>
              <Button 
                variant="outline" 
                className="w-full border-red-500/50 text-red-500 hover:bg-red-500/10"
                onClick={handleCancel}
                disabled={isCancelling}
              >
                {isCancelling ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Cancelling...
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4 mr-2" />
                    Cancel Processing
                  </>
                )}
              </Button>
            </div>
          )}
          
          {isComplete && (
            <p className="text-sm text-center text-green-500">
              Your track is ready to edit!
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
